import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import type { ProjectResult } from '../api/types';
import {
  BUCKET_MEANING_FA,
  toInspectDetail,
  type InspectCoverageBucket,
} from '../lib/inspect-detail';
import { inspectResultColumnConfig } from '../config/inspectResultColumnConfig';
import { guideTooltipHtml } from './CoverageHelpTip';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import {
  InspectResultsBulkMenu,
  type BulkNotify,
} from './InspectBulkMenu';
import { IndexerAgGrid, INDEXER_AG_GRID_FILL_HEIGHT } from './IndexerAgGrid';
import { IndexerIcon } from './IndexerIcon';
import { UrlInspectHistory } from './UrlInspectHistory';

type FilterKey = 'all' | InspectCoverageBucket;

type FilterGuide = {
  title: string;
  paragraphs: string[];
};

const FILTER_GUIDES: Record<FilterKey, FilterGuide> = {
  all: {
    title: 'همه',
    paragraphs: [
      BUCKET_MEANING_FA,
      'این خودش یک وضعیت واقعی نیست؛ یعنی «بدون فیلتر».',
      'همهٔ نتایج این درخواست را با هم نشان می‌دهد؛ چه ایندکس‌شده، چه ایندکس‌نیست، چه خطا.',
    ],
  },
  indexed: {
    title: 'ایندکس‌شده',
    paragraphs: [
      BUCKET_MEANING_FA,
      'این فیلتر همان bucket ایندکس‌شده است: وقتی درخواست موفق بوده (success = true) و از جواب گوگل معلوم شود صفحه در ایندکس است.',
      'تشخیص به ترتیب این‌طور است: اول اگر فیلد verdict دقیقاً برابر PASS باشد، صفحه ایندکس‌شده حساب می‌شود — یعنی گوگل صریحاً گفته صفحه قبول/ایندکس است.',
      'اگر PASS نبود، به متن coverageState نگاه می‌شود: اگر داخل متن کلمهٔ indexed باشد ولی عبارت not indexed نباشد، باز هم ایندکس‌شده حساب می‌شود.',
      'مثال‌های رایج: verdict = PASS، یا coverage مثل Submitted and indexed.',
    ],
  },
  excluded: {
    title: 'ایندکس‌نیست',
    paragraphs: [
      BUCKET_MEANING_FA,
      'این فیلتر همان bucket ایندکس‌نیست (excluded) است: وقتی درخواست موفق بوده، ولی شرط ایندکس‌شده برقرار نیست.',
      'برای فهرست coverageStateهای رایج این bucket، آیکون راهنما را ببینید.',
    ],
  },
  error: {
    title: 'خطا',
    paragraphs: [
      BUCKET_MEANING_FA,
      'این فیلتر همان bucket خطا است: وقتی خودِ درخواست به گوگل موفق نباشد.',
      'مثال‌ها: سهمیه تمام شده، کلید سرویس مشکل دارد، دسترسی Search Console درست نیست، شبکه یا API خطا داده.',
      'اینجا اصلاً جواب معتبری از گوگل برای «ایندکس هست یا نه» نداریم؛ فقط می‌دانیم درخواست شکست خورده.',
      'در عمل: اگر success برابر false باشد → خطا.',
    ],
  },
};

function FilterBucketHelp({ filterKey }: { filterKey: FilterKey }) {
  const guide = FILTER_GUIDES[filterKey];
  return (
    <HelpTooltip
      html={guideTooltipHtml(guide)}
      placement="bottom"
      icon="info-circle"
      className="ms-1"
    />
  );
}

export function InspectResultTable({
  projectId,
  results,
  onNotify,
  fillHeight = false,
}: {
  projectId: string;
  results: ProjectResult[];
  onNotify?: BulkNotify;
  fillHeight?: boolean;
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [historyUrl, setHistoryUrl] = useState<string | null>(null);

  const details = useMemo(() => results.map(toInspectDetail), [results]);
  const counts = useMemo(() => {
    const next = { all: details.length, indexed: 0, excluded: 0, error: 0 };
    for (const item of details) next[item.bucket] += 1;
    return next;
  }, [details]);
  const rows = useMemo(
    () => (filter === 'all' ? details : details.filter((item) => item.bucket === filter)),
    [details, filter],
  );

  const columns = useMemo(
    () =>
      inspectResultColumnConfig({
        navigate,
        onHistory: (url) => setHistoryUrl(url),
      }),
    [navigate],
  );

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: { resultId?: string; url: string } }) =>
        params.data.resultId ?? params.data.url,
      rowHeight: 40,
      animateRows: false,
      suppressCellFocus: true,
      onCellClicked: (event: {
        colDef?: { field?: string };
        data?: { url?: string };
      }) => {
        if (event.colDef?.field !== 'url' || !event.data?.url) return;
        setHistoryUrl(event.data.url);
      },
    }),
    [],
  );

  if (results.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-5">
        <IndexerIcon name="table" />
        <p className="mb-0">هنوز نتیجه‌ای ثبت نشده است.</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={
          fillHeight ? 'indexer-fill-grid if-result-grid' : 'if-result-grid'
        }
      >
        <div
          className={`d-flex flex-wrap justify-content-between align-items-center gap-2 pb-2${
            fillHeight ? ' indexer-fill-grid-toolbar' : ' px-3 pt-3'
          }`}
        >
          <ButtonGroup size="sm">
            {(
              [
                ['all', `همه (${counts.all})`],
                ['indexed', `ایندکس‌شده (${counts.indexed})`],
                ['excluded', `ایندکس‌نیست (${counts.excluded})`],
                ['error', `خطا (${counts.error})`],
              ] as const
            ).map(([value, label]) => (
              <ToggleButton
                key={value}
                id={`inspect-filter-${value}`}
                type="radio"
                variant="outline-secondary"
                name="inspect-filter"
                value={value}
                checked={filter === value}
                onChange={() => setFilter(value)}
              >
                <span className="d-inline-flex align-items-center gap-1">
                  {label}
                  <FilterBucketHelp filterKey={value} />
                </span>
              </ToggleButton>
            ))}
          </ButtonGroup>
          <InspectResultsBulkMenu
            results={results}
            indexedCount={counts.indexed}
            notIndexedCount={counts.excluded + counts.error}
            onNotify={onNotify}
          />
        </div>
        {rows.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-4">
            <p className="mb-0">نتیجه‌ای با این فیلتر نیست.</p>
          </div>
        ) : (
          <div className={fillHeight ? 'indexer-fill-grid-body' : undefined}>
            <IndexerAgGrid
              tableName="indexer_inspect_results"
              columns={columns}
              data={rows}
              height={fillHeight ? INDEXER_AG_GRID_FILL_HEIGHT : undefined}
              otherOptions={otherOptions}
            />
          </div>
        )}
      </div>
      {historyUrl && (
        <UrlInspectHistory
          projectId={projectId}
          url={historyUrl}
          show
          onHide={() => setHistoryUrl(null)}
        />
      )}
    </>
  );
}
